import Label from "@/components/Label/Label";
import IMenuItem from "@/interfaces/Props/IMenuItem";
import {
  selectMenu,
  setActiveItem,
} from "@/redux/features/Setting/Menu/menuSlice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  Box,
  ListItemButton,
  ListItemText,
  SvgIcon,
  useTheme,
} from "@mui/material";
import { forwardRef, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const NavItem = ({
  item,
  level,
  isGroupItem = false,
}: {
  item: IMenuItem;
  level: number;
  isGroupItem?: boolean;
}) => {
  const theme = useTheme();

  const dispatch = useAppDispatch();

  const { pathname } = useLocation();

  const { drawerOpen, openItem } = useAppSelector(selectMenu);

  const darkMode = false;

  const [selectColor, setSelectColor] = useState(
    darkMode ? `secondary.800` : `secondary.200`
  );

  let itemTarget = "_self";
  if (item.target) {
    itemTarget = "_blank";
  }

  let listItemProps = {
    component: forwardRef<HTMLAnchorElement>((props, ref) => (
      <Link ref={ref} {...props} to={item.url!} target={itemTarget} />
    )),
  };

  const itemHandler = (id: string) => {
    dispatch(setActiveItem([id]));
  };

  const isSelected = openItem.findIndex((id) => id === item.id) > -1;
  // active menu item on page load
  useEffect(() => {
    if (item.url && pathname.includes(item.url)) {
      dispatch(setActiveItem([item.id]));
    }
    // eslint-disable-next-line
  }, [pathname]);

  const textColor = `${theme.palette.primary.contrastText}`;

  useEffect(() => {
    setSelectColor(darkMode ? `secondary.800` : `secondary.200`);
  }, [darkMode]);

  return (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      onClick={() => itemHandler(item.id)}
      selected={isSelected}
      sx={{
        zIndex: 1201,
        pl: drawerOpen ? (isGroupItem ? 2 : 6) : 1.5,
        py: !drawerOpen && level === 1 ? 1.25 : 1,
        ...(drawerOpen && {
          "&:hover": {
            bgcolor: "primary.lighter",
          },
          "&.Mui-selected": {
            bgcolor: "primary.lighter",
            borderRight: `2px solid ${theme.palette.primary.main}`,
            color: selectColor,
            "&:hover": {
              color: selectColor,
              bgcolor: "primary.lighter",
            },
          },
        }),
        ...(!drawerOpen && {
          "&:hover": {
            bgcolor: "transparent",
          },
          "&.Mui-selected": {
            "&:hover": {
              bgcolor: "transparent",
            },
            bgcolor: "transparent",
          },
        }),
      }}
    >
      {item.icon ? (
        <SvgIcon
          sx={{ color: isSelected ? selectColor : textColor }}
          fontSize="small"
        >
          {" "}
          {item.icon}{" "}
        </SvgIcon>
      ) : (
        <Box
          sx={{
            backgroundColor: isSelected ? selectColor : textColor,
            height: "24px",
            width: "2px",
          }}
        />
      )}
      {(drawerOpen || (!drawerOpen && level !== 1)) && (
        <>
          <ListItemText
            sx={{ pl: 2 }}
            primary={
              <Label
                id={item.title}
                variant={isGroupItem ? "subtitle1" : "h6"}
                sx={{ color: isSelected ? selectColor : textColor }}
              />
            }
          ></ListItemText>
        </>
      )}
    </ListItemButton>
  );
};

export default NavItem;
